import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShoppingCart, Trash2, Send, Plus, Minus, ArrowLeft, Search, Package, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '5594991858158';

export const Orcamento = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCatalog, setShowCatalog] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    city: '',
    message: ''
  });

  // Buscar produtos, marcas e categorias do backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, brandsRes, categoriesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products`),
          axios.get(`${BACKEND_URL}/api/brands`),
          axios.get(`${BACKEND_URL}/api/categories`)
        ]);
        
        if (productsRes.data?.length > 0) setProducts(productsRes.data);
        if (brandsRes.data?.length > 0) setBrands(brandsRes.data);
        if (categoriesRes.data?.length > 0) setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os produtos.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [toast]);

  // Adicionar produto da URL se especificado
  useEffect(() => {
    const productId = searchParams.get('produto');
    if (productId && products.length > 0) {
      const product = products.find(p => p.id === productId);
      if (product && !selectedProducts.find(p => p.id === product.id)) {
        setSelectedProducts([{ ...product, quantity: 1 }]);
      }
    }
  }, [searchParams, products, selectedProducts]);

  const addProduct = (product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, { ...product, quantity: 1 }]);
      toast({ title: `✓ ${product.name} adicionado ao orçamento!` });
    } else {
      toast({ title: "Este produto já está no orçamento", variant: "destructive" });
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setSelectedProducts(
      selectedProducts.map(p => {
        if (p.id === productId) {
          const newQty = Math.max(1, p.quantity + delta);
          return { ...p, quantity: newQty };
        }
        return p;
      })
    );
  };

  // Função para formatar telefone automaticamente
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      return numbers.slice(0, 11).replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  // Função para formatar CPF/CNPJ automaticamente
  const formatCpfCnpj = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      return numbers
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  };

  const handleCpfCnpjChange = (e) => {
    const formatted = formatCpfCnpj(e.target.value);
    setFormData({ ...formData, cpfCnpj: formatted });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProducts.length === 0) {
      toast({
        title: "Atenção",
        description: "Adicione pelo menos um produto ao orçamento.",
        variant: "destructive"
      });
      return;
    }

    // Track quote request
    try {
      await axios.post(`${BACKEND_URL}/api/track/quote?customer_name=${encodeURIComponent(formData.name)}&customer_phone=${encodeURIComponent(formData.phone)}`, null, {
        params: { products: selectedProducts.map(p => p.name) }
      });
    } catch (e) {
      console.log('Track error:', e);
    }

    const productList = selectedProducts
      .map(p => `• ${p.name} (${p.brand}) - Qtd: ${p.quantity}`)
      .join('\n');
    
    const whatsappMessage = encodeURIComponent(
      `*SOLICITAÇÃO DE ORÇAMENTO*\n\n` +
      `*Cliente:* ${formData.name}\n` +
      `*CPF/CNPJ:* ${formData.cpfCnpj || 'Não informado'}\n` +
      `*Email:* ${formData.email}\n` +
      `*Telefone:* ${formData.phone}\n` +
      `*Cidade:* ${formData.city || 'Não informada'}\n\n` +
      `*Produtos:*\n${productList}\n\n` +
      `*Observações:* ${formData.message || 'Nenhuma'}`
    );

   window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

    toast({
      title: "Orçamento enviado!",
      description: "Você será redirecionado para o WhatsApp.",
    });
  };

  // Filtrar produtos
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = !selectedBrand || p.brand === selectedBrand;
    const matchesCategory = !selectedCategory || p.category === selectedCategory ||
                           categories.find(c => c.id === selectedCategory)?.name?.toLowerCase() === p.category?.toLowerCase();
    return matchesSearch && matchesBrand && matchesCategory;
  });

  // Extrair marcas únicas dos produtos
  const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/catalogo" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-2">
              <ArrowLeft size={18} className="mr-1" /> Voltar ao Catálogo
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Solicitar Orçamento</h1>
            <p className="text-gray-600">Selecione os produtos e preencha seus dados</p>
          </div>
          {selectedProducts.length > 0 && (
            <div className="bg-red-600 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2">
              <ShoppingCart size={20} />
              {selectedProducts.length} {selectedProducts.length === 1 ? 'item' : 'itens'}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Coluna Esquerda - Catálogo de Produtos */}
          <div className="lg:col-span-2 space-y-6">
            {/* Barra de Busca e Filtros */}
            <Card className="bg-white">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      placeholder="Buscar produtos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    variant={showCatalog ? "default" : "outline"}
                    onClick={() => setShowCatalog(!showCatalog)}
                    className="whitespace-nowrap"
                  >
                    {showCatalog ? 'Ocultar Catálogo' : 'Mostrar Catálogo'}
                  </Button>
                </div>
                
                {/* Filtros */}
                <div className="flex flex-wrap gap-3">
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="flex h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todas as Marcas</option>
                    {uniqueBrands.map((brand) => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                  
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="flex h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todas as Categorias</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  
                  {(selectedBrand || selectedCategory) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedBrand(''); setSelectedCategory(''); }}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <X size={16} className="mr-1" /> Limpar filtros
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Grid de Produtos */}
            {showCatalog && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {loading ? (
                  <div className="col-span-2 text-center py-12">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-500">Carregando produtos...</p>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="col-span-2 text-center py-12">
                    <Package size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">Nenhum produto encontrado</p>
                  </div>
                ) : (
                  filteredProducts.map((product) => {
                    const isSelected = selectedProducts.some(p => p.id === product.id);
                    return (
                      <Card 
                        key={product.id} 
                        className={`overflow-hidden transition-all ${isSelected ? 'ring-2 ring-green-500 bg-green-50' : 'hover:shadow-lg'}`}
                      >
                        <div className="flex">
                          <div className="w-28 h-28 bg-gray-100 flex-shrink-0">
                            <img
                              src={product.images?.[0] || 'https://placehold.co/150x150?text=Produto'}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <CardContent className="p-3 flex-1 flex flex-col">
                            <Badge className="self-start mb-1 text-xs bg-blue-900">{product.brand}</Badge>
                            <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-2">{product.name}</h3>
                            <div className="mt-auto">
                              {isSelected ? (
                                <div className="flex items-center justify-between">
                                  <span className="text-green-600 text-sm font-medium">✓ No orçamento</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                                    onClick={() => removeProduct(product.id)}
                                  >
                                    <X size={18} />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => addProduct(product)}
                                >
                                  <Plus size={16} className="mr-1" /> Adicionar
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Coluna Direita - Carrinho e Formulário */}
          <div className="space-y-6">
            {/* Produtos Selecionados */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <ShoppingCart size={20} className="text-red-600" />
                  Produtos no Orçamento
                </h2>
                
                {selectedProducts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={40} className="mx-auto mb-3 opacity-50" />
                    <p>Nenhum produto selecionado</p>
                    <p className="text-sm">Clique em "Adicionar" nos produtos ao lado</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedProducts.map((product) => (
                      <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <img
                          src={product.images?.[0] || 'https://placehold.co/60x60?text=P'}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.brand}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={() => updateQuantity(product.id, -1)}
                          >
                            <Minus size={14} />
                          </Button>
                          <span className="w-8 text-center font-medium">{product.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={() => updateQuantity(product.id, 1)}
                          >
                            <Plus size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeProduct(product.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Formulário de Dados */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Seus Dados</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Seu nome"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone *</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        required
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">CPF/CNPJ</label>
                      <Input
                        name="cpfCnpj"
                        value={formData.cpfCnpj}
                        onChange={handleCpfCnpjChange}
                        placeholder="000.000.000-00"
                        maxLength={18}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Cidade</label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Ex: Canaã dos Carajás - PA"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Observações</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Informações adicionais..."
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
                    disabled={selectedProducts.length === 0}
                  >
                    <Send size={20} className="mr-2" />
                    Enviar Orçamento via WhatsApp
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
